import api from './api';
import { queueAction, storeOfflineData, getOfflineData } from './syncService';
import {
    JobCard,
    CreateJobCardRequest,
    CreateTaskRequest,
    CreateSignatureRequest,
    UpdateTaskRequest,
    Task,
    Signature
} from '../types/jobcard';


// Helper function to check online status
const isOnline = () => navigator.onLine;

// API Functions
export async function getJobCards(
    search?: string,
    closed?: boolean | 'true' | 'false' | 1 | 0,
    per_page?: number,
    params?: {
        start_date?: string;
        end_date?: string;
        store_id?: string;
        user_id?: string | number | (string | number)[];
        department_id?: string | number;
        allocated?: boolean;
        page?: number;
    }
) {
    const queryParts: string[] = [];

    if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

    // Handle closed parameter more explicitly
    if (closed !== undefined) {
        const closedParam =
            closed === true || closed === 'true' || closed === 1 ? '1' : '0';
        queryParts.push(`closed=${closedParam}`);
    }

    if (per_page) queryParts.push(`per_page=${per_page}`);

    // Add additional filter parameters
    if (params) {
        if (params.start_date) queryParts.push(`start_date=${encodeURIComponent(params.start_date)}`);
        if (params.end_date) queryParts.push(`end_date=${encodeURIComponent(params.end_date)}`);
        if (params.store_id) queryParts.push(`store_id=${encodeURIComponent(params.store_id)}`);
        if (params.department_id) queryParts.push(`department_id=${encodeURIComponent(params.department_id)}`);
        if (params.allocated) queryParts.push(`allocated=${params.allocated ? '1' : '0'}`);
        if (params.page) queryParts.push(`page=${params.page}`);
        
        // Handle user_id (string, number, or array)
        if (params.user_id) {
            if (Array.isArray(params.user_id)) {
                params.user_id.forEach(id => {
                    queryParts.push(`user_id[]=${encodeURIComponent(id)}`);
                });
            } else {
                queryParts.push(`user_id=${encodeURIComponent(params.user_id)}`);
            }
        }
    }

    const queryString = queryParts.join('&');

    return api.get<{
        data: JobCard[] | {
            data: JobCard[];
            current_page: number;
            last_page: number;
            total: number;
        };
        success: boolean
    }>(`/jobcards?${queryString}`);
}

export async function createJobCard(data: CreateJobCardRequest) {
    if (!isOnline()) {
        const id = await queueAction('/jobcards', 'POST', data);
        const offlineJobCard = {
            ...data,
            id: `offline_${id}`,
            created_at: new Date().toISOString(),
            tasks: [],
            signatures: []
        };
        const existingJobCards = await getOfflineData('jobcards') || [];
        await storeOfflineData('jobcards', [offlineJobCard, ...existingJobCards]);
        return { data: offlineJobCard, success: true };
    }

    return api.post<{ data: JobCard; success: boolean; message: string }>('/jobcards', data);
}

export async function updateTask(jobCardId: number, taskId: number, data: UpdateTaskRequest) {
    if (!isOnline()) {
        await queueAction(`/jobcards/${jobCardId}/tasks/${taskId}`, 'POST', data);
        // Update local data
        const jobCards = await getOfflineData('jobcards') || [];
        const updatedJobCards = jobCards.map((jobCard: JobCard) => {
            if (jobCard.id === jobCardId) {
                return {
                    ...jobCard,
                    tasks: (jobCard.tasks || []).map(task =>
                        task.id === taskId ? { ...task, ...data } : task
                    )
                };
            }
            return jobCard;
        });
        await storeOfflineData('jobcards', updatedJobCards);
        return { success: true, message: 'Task updated (offline)', data: null };
    }

    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('job_id', taskId.toString());

    if (data.pictures) {
        data.pictures.forEach((picture, index) => {
            formData.append(`pictures[${index}]`, picture);
        });
    }

    if (data.delete_pictures) {
        data.delete_pictures.forEach((id, index) => {
            formData.append(`delete_pictures[${index}]`, id.toString());
        });
    }

    // Add equipment if it exists
    if (data.equipment && data.equipment.length > 0) {
        data.equipment.forEach((item, index) => {
            formData.append(`equipment[${index}][equipment_id]`, item.equipment_id.toString());
            formData.append(`equipment[${index}][allocation_type]`, item.allocation_type);
            if (item.allocated_id !== undefined && item.allocated_id !== null) {
                formData.append(`equipment[${index}][allocated_id]`, item.allocated_id.toString());
            }
            if (item.store_number) {
                formData.append(`equipment[${index}][store_number]`, item.store_number);
            }
            formData.append(`equipment[${index}][allocation_date]`, item.allocation_date);
            if (item.allocation_notes) {
                formData.append(`equipment[${index}][allocation_notes]`, item.allocation_notes);
            }
            // Add quantity field
            formData.append(`equipment[${index}][quantity]`, (item.quantity || 1).toString());
            
            // Add grouped_ids if available
            if (item.groupedIds && item.groupedIds.length > 0) {
                item.groupedIds.forEach((groupId, groupIndex) => {
                    formData.append(`equipment[${index}][grouped_ids][${groupIndex}]`, groupId.toString());
                });
            }
        });
    }

    const response = await api.post<{ success: boolean; message: string; data: Task }>(
        `/jobcards/${jobCardId}/tasks/${taskId}`,
        formData,
        {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data'
            },
        }
    );

    return response;
}

export async function deleteJobCard(jobCardId: number) {
    if (!isOnline()) {
        await queueAction(`/jobcards/${jobCardId}`, 'DELETE', {});
        const jobCards = await getOfflineData('jobcards') || [];
        await storeOfflineData('jobcards', jobCards.filter((jobCard: JobCard) => jobCard.id !== jobCardId));
        return { success: true, message: 'Job card deleted (offline)' };
    }

    return api.delete<{ success: boolean; message: string }>(`/jobcards/${jobCardId}`);
}

export async function createSignature(jobCardId: number, data: CreateSignatureRequest) {
    if (!isOnline()) {
        await queueAction(`/jobcards/${jobCardId}/signatures`, 'POST', data);
        return { success: true, message: 'Signature created (offline)' };
    }

    const formData = new FormData();
    formData.append('signatures', data.signatures);
    formData.append('name', data.name);
    formData.append('is_manager', data.is_manager ? '1' : '0');

    return api.post<{ success: boolean; message: string }>(`/jobcards/${jobCardId}/signatures`, formData, {
        headers: {
            Accept: 'application/json'
        }
    });
}

export async function createTask(jobCardId: number, data: CreateTaskRequest) {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('job_id', jobCardId.toString());

    if (data.pictures) {
        data.pictures.forEach((picture, index) => {
            formData.append(`pictures[${index}]`, picture);
        });
    }

    // Add equipment if it exists
    if (data.equipment && data.equipment.length > 0) {
        data.equipment.forEach((item, index) => {
            formData.append(`equipment[${index}][equipment_id]`, item.equipment_id.toString());
            formData.append(`equipment[${index}][allocation_type]`, item.allocation_type);
            if (item.allocated_id !== undefined && item.allocated_id !== null) {
                formData.append(`equipment[${index}][allocated_id]`, item.allocated_id.toString());
            }
            if (item.store_number) {
                formData.append(`equipment[${index}][store_number]`, item.store_number);
            }
            formData.append(`equipment[${index}][allocation_date]`, item.allocation_date);
            if (item.allocation_notes) {
                formData.append(`equipment[${index}][allocation_notes]`, item.allocation_notes);
            }
            // Add quantity field
            formData.append(`equipment[${index}][quantity]`, (item.quantity || 1).toString());
            
            // Add grouped_ids if available
            if (item.groupedIds && item.groupedIds.length > 0) {
                item.groupedIds.forEach((groupId, groupIndex) => {
                    formData.append(`equipment[${index}][grouped_ids][${groupIndex}]`, groupId.toString());
                });
            }
        });
    }

    return api.post<{ success: boolean; message: string; data: Task }>(
        `/jobcards/${jobCardId}/tasks`,
        formData,
        {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data'
            }
        }
    );
}

export async function deleteTask(jobCardId: number, taskId: number) {
    if (!isOnline()) {
        await queueAction(`/jobcards/${jobCardId}/tasks/${taskId}`, 'DELETE', {});
        const jobCards = await getOfflineData('jobcards') || [];
        const updatedJobCards = jobCards.map((jobCard: JobCard) => {
            if (jobCard.id === jobCardId) {
                return {
                    ...jobCard,
                    tasks: (jobCard.tasks || []).filter(task => task.id !== taskId)
                };
            }
            return jobCard;
        });
        await storeOfflineData('jobcards', updatedJobCards);
        return { success: true, message: 'Task deleted (offline)' };
    }

    return api.delete<{ success: boolean; message: string }>(`/jobcards/${jobCardId}/tasks/${taskId}`);
}

export async function getJobCard(id: number) {
    if (!isOnline()) {
        const jobCards = await getOfflineData('jobcards') || [];
        const jobCard = jobCards.find((card: JobCard) => card.id === id);
        if (jobCard) {
            return { data: { data: jobCard }, success: true };
        }
        throw new Error('Job card not found offline');
    }

    return api.get<{ data: JobCard; success: boolean }>(`/jobcards/${id}`);
}

export async function updateJobCard(id: number, data: CreateJobCardRequest, config?: { timeout?: number }) {
    if (!isOnline()) {
        await queueAction(`/jobcards/${id}`, 'PUT', data);
        const jobCards = await getOfflineData('jobcards') || [];
        const updatedJobCards = jobCards.map((jobCard: JobCard) =>
            jobCard.id === id ? { ...jobCard, ...data } : jobCard
        );
        await storeOfflineData('jobcards', updatedJobCards);
        return { success: true, message: 'Job card updated (offline)' };
    }

    return api.put<{ success: boolean; message: string }>(`/jobcards/${id}`, data, config);
}

export async function getTaskImages(taskId: number) {
    const response = await api.get<{
        success: boolean;
        data: Array<{
            id: number;
            picture: string;
            url: string;
            created_at: string;
        }>;
    }>(`/tasks/${taskId}/images`);
    return response.data;
}

export async function getTasks(jobCardId: number) {
    if (!isOnline()) {
        const jobCards = await getOfflineData('jobcards') || [];
        const jobCard = jobCards.find((card: JobCard) => card.id === jobCardId);
        return { data: jobCard?.tasks || [] };
    }

    const response = await api.get<Task[]>(`/jobcards/${jobCardId}/tasks`);
    
    // Fetch pictures for each task
    const tasksWithPictures = await Promise.all(
        response.data.map(async (task) => {
            try {
                const picturesResponse = await getTaskImages(task.id);
                return {
                    ...task,
                    pictures: picturesResponse.data.map(pic => ({
                        id: pic.id,
                        picture: pic.picture,
                        url: pic.url
                    }))
                };
            } catch (error) {
                console.error(`Error fetching pictures for task ${task.id}:`, error);
                return {
                    ...task,
                    pictures: []
                };
            }
        })
    );

    return { data: tasksWithPictures };
}

export async function getSignatures(jobCardId: number) {
    if (!isOnline()) {
        const jobCards = await getOfflineData('jobcards') || [];
        const jobCard = jobCards.find((card: JobCard) => card.id === jobCardId);
        return { data: { data: jobCard?.signature || [] }, success: true };
    }

    return api.get<{ success: boolean; data: { data: Signature[] } }>(`/jobcards/${jobCardId}/signatures`);
}

// Alias for consistency with the signatures page
export const getJobCardSignatures = getSignatures;

// Add similar offline handling for other functions...

export const deleteSignature = async (signatureId: number, jobCardId: number) => {
    console.log('Frontend: Deleting signature', {
        signatureId,
        jobCardId,
        url: `/jobcards/${jobCardId}/signatures/${signatureId}`
    });
    
    const response = await api.delete(`/jobcards/${jobCardId}/signatures/${signatureId}`);
    return response.data;
};

export const getJobCardEquipment = async (id: number) => {
    return api.get(`/jobcards/${id}/equipment`);
};

export const attachEquipment = (jobCardId: number, equipmentId: number) => {
    return api.post(`/jobcards/${jobCardId}/equipment/${equipmentId}`);
};

export const detachEquipment = (jobCardId: number, equipmentId: number) => {
    return api.delete(`/jobcards/${jobCardId}/equipment/${equipmentId}`);
};

export const getATRItems = async (id: number) => {
    return api.get(`/jobcards/${id}/atr-items`);
};

export const completeATRItem = async (id: number, createTask: boolean = false, completionAction?: 'repaired' | 'received') => {
    return api.post(`/jobcards/atr-items/${id}`, { createTask, completionAction });
};

export const updateATREquipmentSerialNumber = async (id: number, serialNumber: string) => {
    return api.post(`/atr-equipment/${id}/update-serial`, { serial_number: serialNumber });
};

export const deleteTaskPicture = async (taskId: number, pictureId: number) => {
    return api.delete<{ success: boolean; message: string }>(`/jobcards/tasks/${taskId}/pictures/${pictureId}`);
};

export const storeATRItem = async (jobCardId: number, data: {
    asset: string;
    equipment_id?: number;
    description?: string;
}) => {
    return await api.post(`/jobcards/${jobCardId}/atr-items`, data);
};

export const syncATRItems = async (jobCardId: number) => {
    return api.post(`/jobcards/${jobCardId}/sync-atr-items`);
};

export const checkExistingJobCard = async (orderNumber: string) => {
    return api.get<{ 
        exists: boolean; 
        valid?: boolean; 
        jobCard?: JobCard; 
        message?: string; 
        error?: string; 
        warning?: string;
        fallback_behavior?: string;
    }>(`/jobcards/check-existing/${orderNumber}`);
};

export const syncAllJobCards = async () => {
    return api.post<{ 
        success: boolean; 
        message: string;
        syncedCount: number;
        updatedCount: number;
        deletedCount: number;
        totalCount: number;
        errors: string[];
        warnings: string[];
    }>('/jobcards/sync-all');
};

export const completeJobCardAsync = async (id: number) => {
    if (!isOnline()) {
        // For offline mode, we'll still use the regular completion
        await queueAction(`/jobcards/${id}`, 'PUT', { closed: true });
        return { success: true, message: 'Job card completion queued (offline)' };
    }

    return api.post<{ success: boolean; message: string }>(`/jobcards/${id}/complete-async`);
}

// Job Card Linking API functions
export const searchLinkableJobCards = async (id: number, search?: string) => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.get<{
        success: boolean;
        data: JobCard[];
        po_job_card: JobCard;
    }>(`/jobcards/${id}/linkable-jobcards${params}`);
};

export const linkJobCards = async (id: number, targetJobCardId: number) => {
    return api.post<{
        success: boolean;
        message: string;
        data: {
            linked_job_card: JobCard;
            can_undo_until: string;
        };
    }>(`/jobcards/${id}/link`, {
        target_job_card_id: targetJobCardId,
        confirmation: true
    });
};

export const undoLinkJobCards = async (id: number) => {
    return api.post<{
        success: boolean;
        message: string;
        data: {
            restored_po_job_card_id: number;
            restored_completed_job_card: JobCard;
        };
    }>(`/jobcards/${id}/undo-link`);
};

export const canUndoLink = async (id: number) => {
    return api.get<{
        success: boolean;
        can_undo: boolean;
        undo_expires_at: string | null;
    }>(`/jobcards/${id}/can-undo-link`);
};

export default {
    getJobCards,
    createJobCard,
    updateTask,
    deleteJobCard,
    createSignature,
    createTask,
    deleteTask,
    getJobCard,
    updateJobCard,
    getTasks,
    getSignatures,
    getJobCardSignatures,
    deleteTaskPicture,
    storeATRItem,
    syncATRItems,
    syncAllJobCards,
    checkExistingJobCard,
    // ... other exports
};
